import * as documentStates from '@/constants/documentStates';
import * as documentStatusOptions from '@/constants/documentStatus';

import axios from 'axios';
import { createStore } from 'vuex';

import AgentsStore from './V2/stores/AgentsStore';
import BulkUploadStore from './V2/stores/BulkUploadStore';
import CollectConfigStore from './V2/stores/CollectConfigStore';
import CollectSessionsStore from './V2/stores/CollectSessionsStore';
import ConfigStore from './V2/stores/ConfigStore';
import CreditStore from './V2/stores/CreditStore';
import CustomerStore from './V2/stores/CustomersStore';
import CustomersSignalsStore from './V2/stores/CustomersSignalsStore';
import DocumentsStore from './V2/stores/DocumentsStore';
import DocumentFontsMetadataStore from './V2/stores/DocumentFontsMetadataStore';
import DocumentHighlighterStore from './V2/stores/DocumentHighlighterStore';
import DocumentParsedDetailsHighlighterStore from './V2/stores/DocumentParsedDetailsHighlighterStore';
import FeatureFlagsStore from './V2/stores/FeatureFlagsStore';
import ProcessingEventsStore from './V2/stores/ProcessingEventsStore';
import QueryStringsStore from './V2/stores/QueryStringsStore';
import SearchStore from './V2/stores/SearchStore';
import SignalsStore from './V2/stores/SignalsStore';
import SimilarDocumentsStore from './V2/stores/SimilarDocumentsStore';
import TagStore from './V2/stores/tagStore';
import { datadogRum } from '@datadog/browser-rum';
import {
  ADVANCED_READONLY_USER_ROLE,
  ADVANCED_USER_ROLE,
  ADMIN_ROLE,
} from '@/constants/userRoles';
import { showToast } from '@inscribe/signal';
import ChatStore from './V2/stores/ChatStore';

export const getters = {
  isOrgAdmin: (state) => state.currentUser.role === ADMIN_ROLE,
  isOrgAdvancedUserOrAdmin: (state) =>
    [ADVANCED_USER_ROLE, ADMIN_ROLE].includes(state.currentUser?.role),
  isOrgAdvancedReadOnlyUser: (state) =>
    ADVANCED_READONLY_USER_ROLE === state.currentUser.role,
  // DEPRECATED - use DocumentsStore instead with getCachedDocumentById and
  // the currently active document ID in the route
  getCurrentUser: (state) => state.currentUser,
  selectedDocument: (state) => {
    return this.getDocumentById(state.selectedDocumentId);
  },
  getDocumentsLoading: (state) => state.documentsLoading,
  // DEPRECATED
  getSelectedCustomer: (state) => {
    return state.customers.selectedCustomer;
  },
  documentsListEmpty: (state) => !state.documents.length,
  getBlocklist: (state) => state.blocklist,
  someDocumentUploading: (state) => (docId) => {
    if (!docId) {
      return undefined;
    }
    return (state.documents || []).some(
      ({ state }) => state === documentStates.UPLOADING,
    );
  },
  someDocumentUploadingOrProcessing: (state) => {
    return (state.documents || []).some(
      ({ state }) =>
        state === documentStates.UPLOADING ||
        state === documentStates.PROCESSING,
    );
  },
  getDocumentBeingDeleted: (state) => (docId) => {
    return state.documentsBeingDeletedById[docId] === true;
  },
  getDocumentById: (state) => (docId) => {
    if (!docId) {
      return undefined;
    }
    return state.documents?.find(
      (doc) => doc.id && doc.id.toString() === docId.toString(),
    );
  },
  getRemainingDocs: (state) => (docId) => {
    if (!docId) {
      return undefined;
    }
    return state.documents.filter(
      (doc) => doc.id && doc.id.toString() !== docId.toString(),
    );
  },
  isOrgAdminAndWorkspacesCreationEnabled: (state, getters) =>
    getters.isOrgAdmin &&
    state.featureFlagsStore.featureFlags.is_workspace_creation_enabled,
};

export const actions = {
  deleteIdentityProvider() {
    return axios.delete('/auth/api/v2/identity_provider/');
  },
  getIdentityProvider(context) {
    return axios.get('/auth/api/v2/identity_provider').then((response) => {
      context.commit('setIdentityProvider', response.data.data);
    });
  },
  createIdentityProvider(context, identityProvider) {
    return axios.post('/auth/api/v2/identity_provider/', identityProvider);
  },
  updateIdentityProvider(context, identityProvider) {
    return axios.put('/auth/api/v2/identity_provider/', identityProvider);
  },
  getSsoConfig() {
    return axios.get('/auth/api/v2/sso_config');
  },
  updateSsoConfig(context, { strict_mode }) {
    return axios.put('/auth/api/v2/sso_config', { strict_mode });
  },
  getCurrentUser(context) {
    return new Promise((resolve, reject) => {
      return axios
        .get('/auth/api/user/')
        .then((response) => {
          if (response?.data?.user) {
            context.commit('addCurrentUser', response.data.user);
            context.commit('setFeatureFlags', response.data.user);
            datadogRum.setUser({
              id: response.data.user.id,
              name: response.data.user.name,
              email: response.data.user.email,
              workspace: response.data.user.organisation.name,
            });

            return resolve(response.data.user);
          } else {
            return resolve(null);
          }
        })
        .catch((response) => {
          context.commit('setUserNotCompletedRegistration');
          return reject(response);
        });
    });
  },
  getDocumentCompare(context, { customerId, documentId, documentXId }) {
    context.commit('setDocumentDifferenceLoading', true);
    return axios
      .get(
        `/api/v2/customers/${customerId}/documents/${documentId}/compare?other_document_id=${documentXId}`,
      )
      .then((response) => {
        context.commit('addDocumentDifference', response.data.data);
        context.commit('setDocumentDifferenceLoading', false);
        return response;
      })
      .catch(() => {
        context.commit('setDocumentDifferenceLoading', false);
      });
  },
  getTemplates(context) {
    return axios.get(`/api/v2/templates`).then((response) => {
      context.commit('addTemplates', response.data.data);
    });
  },
  deleteTemplate(context, id) {
    return axios.delete(`/api/v2/templates/${id}`);
  },
  addTemplate(context, templateFiles) {
    return axios.post(`/api/v2/templates`, templateFiles);
  },
  getAllCustomersExportCsvReport(content, queryParams) {
    const url = `/api/v2/customers/exportcsvreport`;
    return axios.get(url, { params: queryParams });
  },
  getDocument(context, { customerId, documentId }) {
    return axios.get(`/api/v2/customers/${customerId}/documents/${documentId}`);
  },
  getDocumentOcr(context, { customerId, documentId }) {
    return axios.get(
      `/api/v2/customers/${customerId}/documents/${documentId}/ocr/`,
    );
  },
  deleteDocumentTag(context, { document, tagId }) {
    context.commit('invalidateDocumentCacheByID', document.customer_id);
    return axios.delete(
      `/api/v2/customers/${document.customer_id}/documents/${document.id}/tags/${tagId}`,
    );
  },
  trySetDocumentStatus(context, { document, data }) {
    return new Promise((resolve, reject) => {
      let newStatus = data.review_status;
      if (document.review_status === data.review_status) {
        newStatus = documentStatusOptions.REQUIRES_REVIEW;
      }
      const originalStatus = document.review_status;

      // make call
      document.review_status = newStatus;
      context
        .dispatch('updateDocument', {
          document: document,
          data: { review_status: newStatus },
        })
        .then(
          () => {
            // update generic list
            context.commit('updateGeneric', {
              objectsName: 'documents',
              updatedObject: {
                ...document,
                review_status: newStatus,
              },
            });
            resolve(document);
          },
          (error) => {
            document.review_status = originalStatus;
            reject(error);
          },
        );
    });
  },
  deleteDocument(context, { customerId, document }) {
    return new Promise((resolve, reject) => {
      if (confirm('Are you sure you want to delete this document?')) {
        context.commit('setDocumentsBeingDeleted', document);
        context
          .dispatch('deleteDocumentApi', {
            customer_id: customerId,
            document_id: document.id,
          })
          .then(
            () => {
              showToast({
                text: 'Document successfully deleted.',
              });
              context.commit('deleteGeneric', {
                objectsName: 'documents',
                idToDelete: document.id,
              });
              context.commit('invalidateDocumentCacheByID', customerId);
              context.commit('removeDocumentsBeingDeleted', document);
              resolve(true);
            },
            (errorResponse) => {
              if (
                errorResponse.response !== undefined &&
                errorResponse.response.status === 403
              ) {
                showToast({
                  text: 'You do not have permission to delete this document. Please contact your admin if you require this permission.',
                  isError: true,
                });
              } else {
                showToast({
                  text: 'Could not delete document, please try again or contact Inscribe support.',
                  isError: true,
                });
              }
              context.commit('removeDocumentsBeingDeleted', document);
              reject(errorResponse);
            },
          );
      } else {
        resolve(false);
      }
    });
  },
  updateDocument(context, { document, data }) {
    context.commit('invalidateDocumentCacheByID', document.customer_id);
    return axios.put(
      `/api/v2/customers/${document.customer_id}/documents/${document.id}`,
      data,
    );
  },
  uploadCustomerFiles(context, options) {
    return new Promise((resolve, reject) => {
      const newCustomerUrl = `api/v2/customers/${options.customerId}/documents`;
      const formData = new FormData();
      for (const file of options.files) {
        formData.append('file', file);
      }
      if (options.tags) {
        formData.append(
          'tags',
          JSON.stringify(
            options.tags.map((tagLabel) => {
              return {
                text: tagLabel,
              };
            }),
          ),
        );
      }
      if (options.uploadIds) {
        formData.append('upload_ids', options.uploadIds);
      }
      return axios.post(newCustomerUrl, formData).then(
        function (response) {
          resolve(response);
        },
        function (error) {
          reject(error);
        },
      );
    });
  },
  getCustomerExportPdfReport(context, ids) {
    return axios.get(`/api/v2/customers/${ids.customer_id}/exportpdfreport`);
  },
  deleteDocumentApi(context, ids) {
    return axios.delete(
      `/api/v2/customers/${ids.customer_id}/documents/${ids.document_id}`,
    );
  },
  getPaginatedDocuments(
    context,
    { customerId, page = 1, style = 'advanced', risk_levels = [] },
  ) {
    const riskLevels =
      typeof risk_levels === 'string' ? risk_levels : risk_levels.join(',');
    if (
      context.state.cachedDocumentsByCustomerId[customerId]?.[page]?.[
        riskLevels
      ]?.[style]
    ) {
      return context.state.cachedDocumentsByCustomerId[customerId][page][
        riskLevels
      ][style];
    }
    const request = axios.get(
      `/api/v2/customers/${customerId}/documents/?style=${style}&page=${page}&risk_levels=${riskLevels}`,
      {
        cancelToken: context.state.axiosCancelSource.token,
      },
    );
    if (!context.state.cachedDocumentsByCustomerId[customerId]) {
      context.state.cachedDocumentsByCustomerId[customerId] = {};
    }
    if (!context.state.cachedDocumentsByCustomerId[customerId][page]) {
      context.state.cachedDocumentsByCustomerId[customerId][page] = {};
    }
    if (
      !context.state.cachedDocumentsByCustomerId[customerId][page][riskLevels]
    ) {
      context.state.cachedDocumentsByCustomerId[customerId][page][riskLevels] =
        {};
    }
    context.state.cachedDocumentsByCustomerId[customerId][page][riskLevels][
      style
    ] = request;
    return request;
  },
  getDocumentsForCustomer(
    context,
    { customerId, queryParams = { style: 'advanced' } },
  ) {
    const url = `/api/v2/customers/${customerId}/documents`;
    return axios.get(url, {
      params: queryParams,
      cancelToken: context.state.axiosCancelSource.token,
    });
  },
  getDocumentWithData(context, { customerId, documentId, advanced = true }) {
    const params = advanced ? '?style=advanced' : '';
    return axios.get(
      `/api/v2/customers/${customerId}/documents/${documentId}/${params}`,
    );
  },
  getDocumentWithSignals(context, { customerId, documentId }) {
    return axios.get(
      `/api/v2/customers/${customerId}/documents/${documentId}?style=advanced`,
    );
  },
  getDocumentStatusHistory(context, { customerId, documentId }) {
    return axios.get(
      `/api/v2/customers/${customerId}/documents/${documentId}/review_status_history`,
    );
  },
  reportDocumentIssue(context, { customerId, documentId, issue }) {
    return axios.post(
      `/api/v2/customers/${customerId}/documents/${documentId}/issue`,
      {
        issue,
      },
    );
  },
  updateNotificationPreferences(
    context,
    { organisationPreferences, browserNotifications },
  ) {
    return axios.put('auth/api/profile/notifications/', {
      organisation_preferences: organisationPreferences,
      browser_notifications: browserNotifications,
    });
  },
  getWebhooks({ commit }) {
    return axios.get(`/api/v2/webhooks`).then((response) => {
      commit('setWebhooks', response.data.data);
    });
  },
  deleteWebhook(context, webhook) {
    return axios.delete(`/api/v2/webhooks/${webhook.id}`);
  },
  addWebhook(context, webhook) {
    return axios.post(`/api/v2/webhooks/`, webhook);
  },
  updateWebhook(context, webhook) {
    return axios.put(`/api/v2/webhooks/${webhook.id}`, webhook);
  },
  testWebhook(context, webhook) {
    return axios.post(`/api/v2/webhooks/${webhook.id}/test`, webhook);
  },
  loadBlocklist(context) {
    axios.get(`/api/v2/blacklists/`).then((response) => {
      context.commit('setBlocklist', response.data.data);
    });
  },
  addBlocklistings(context, blocklistEntry) {
    return axios.post(`/api/v2/blacklistings/`, blocklistEntry);
  },
  tryAddBlocklist(context, blocklistEntry) {
    return axios.post(`/api/v2/blacklists/`, blocklistEntry);
  },
  tryEditBlocklist(context, blocklistEntry) {
    return axios.put(
      `/api/v2/blacklists/${blocklistEntry.id}/`,
      blocklistEntry,
    );
  },
  tryDeleteBlocklist(context, blockList) {
    return axios.delete(`/api/v2/blacklists/${blockList.id}/`);
  },
  requestPasswordReset(context, emailData) {
    return axios.post('/auth/api/password_reset/', emailData);
  },
  resetPassword(context, { passwordResetHash, data }) {
    return axios.post(`/auth/api/password_reset/${passwordResetHash}/`, data);
  },
  joinOrganisation(context, registrationData) {
    return axios.post(`/auth/api/join/`, registrationData);
  },
  login(context, loginData) {
    return axios.post(`/auth/api/login/`, loginData);
  },
  logout() {
    return axios.post(`/auth/logout/`);
  },
  switchWorkspace(context, orgId) {
    return axios.post('/auth/api/switch_workspace/', {
      organisation_id: orgId,
    });
  },
  async getWorkspaces({ commit }) {
    try {
      const response = await axios.get('/auth/api/workspaces');

      commit('setWorkspaces', response.data);
    } catch (error) {
      showToast({
        title: 'Error loading the Workspaces.',
        text: error,
        isError: true,
      });
    }
  },
  async createWorkspace({}, workspaceName) {
    try {
      const response = await axios.post('/auth/api/workspaces/', {
        name: workspaceName,
      });
      showToast({
        title: 'Workspace created successfully ',
        text: `${workspaceName} has been created.`,
      });
      return response;
    } catch (error) {
      showToast({
        title: 'Error creating new Workspace.',
        text: error?.response?.data?.message,
        isError: true,
      });
    }
  },
  samlLogin(context, loginData) {
    return axios.post(`/auth/api/saml_login/`, loginData);
  },
  getOrganisationUsers() {
    return axios.get(`/auth/api/v2/frontend/users`);
  },
  changeUserRole(context, { userId, role }) {
    return axios.put(`/auth/api/user/${userId}/`, { role });
  },
  changeUserActiveStatus(context, { userId, activeStatus }) {
    if (activeStatus === true) {
      return axios.put(`/auth/api/user/${userId}/activate/`);
    } else {
      return axios.put(`/auth/api/user/${userId}/deactivate/`);
    }
  },
  inviteNewUser(context, inviteData) {
    return axios.post('/auth/api/invite/', inviteData);
  },
  reinviteUser(context, userId) {
    return axios.put(`/auth/api/invite/${userId}/`);
  },
  cancelUserInvite(context, userId) {
    return axios.put(`/auth/api/invite/${userId}/cancel/`);
  },
  checkTwoFaStatus() {
    return axios.get('/auth/api/2fa/status/');
  },
  requestOneTouch() {
    return axios.post('/auth/api/2fa/request_onetouch/');
  },
  registerTwoFa(context, data) {
    return axios.post('/auth/api/2fa/', data);
  },
  verifyOtp(context, token) {
    return axios.get(`/auth/api/2fa/verify/${token}`);
  },
  disableTwoFa() {
    return axios.delete(`/auth/api/2fa/`);
  },
  changePassword(context, { oldPassword, newPassword, confirmationPassword }) {
    return axios.post('/auth/api/profile/password/', {
      old_password: oldPassword,
      new_password: newPassword,
      confirmation_password: confirmationPassword,
    });
  },
  generateApiKey(context, data) {
    return axios.post('/auth/api/profile/api_token/', data);
  },
  deactivateApiKey(context, uuid) {
    return axios.delete(`/auth/api/profile/api_token/${uuid}`);
  },
  editApiKey(context, { uuid, keyDetail }) {
    return axios.put(`/auth/api/profile/api_token/${uuid}/`, keyDetail);
  },
};

export const mutations = {
  setCancellationToken(state) {
    state.axiosCancelSource = axios.CancelToken.source();
  },
  invalidateDocumentCacheByID(state, customerId) {
    delete state.cachedDocumentsByCustomerId[customerId];
  },
  setDocumentDifferenceLoading(state, loadingState) {
    state.documentDifferenceIsLoading = loadingState;
  },
  setIdentityProvider(state, identityProvider) {
    state.identityProvider = identityProvider;
  },
  setSelectedIdentityProviderId(state, identityProviderId) {
    state.selectedIdentityProviderId = identityProviderId;
  },
  addDocumentDifference(state, difference) {
    state.documentDifference = difference;
  },
  addCurrentUser(state, user) {
    state.currentUser = user;
    state.hasUserCompletedRegistration = true;
  },
  setUserNotCompletedRegistration(state) {
    state.hasUserCompletedRegistration = false;
  },
  addCompletedAction(state, action) {
    state.currentUser.organisation.actions = [
      ...state.currentUser.organisation.actions,
      { name: action },
    ];
  },
  setWebhooks(state, webhooks) {
    state.webhooks = webhooks;
  },
  addWebhook(state, webhook) {
    state.webhooks.unshift(webhook);
  },
  addBlocklist(state, entry) {
    state.blocklist.unshift(entry);
  },
  addTemplates(state, templates) {
    state.templates = templates;
  },
  addTemplate(state, template) {
    state.templates.unshift(template);
  },
  setBlocklist(state, blocklist) {
    blocklist.map((entry) => (entry.inEdit = false));
    state.blocklist = blocklist;
  },
  deleteGeneric(state, args) {
    state[args.objectsName] = state[args.objectsName].filter((object) => {
      return object.id !== args.idToDelete;
    });
  },
  deleteUploadingDocWithName(state, args) {
    const sameNameDocIndex = state.documents.findIndex((document) => {
      const nameWithoutExtension = (rawName) => {
        const extensionIndex = rawName.lastIndexOf('.');
        return rawName.substr(0, extensionIndex);
      };
      return (
        nameWithoutExtension(document.name) ===
          nameWithoutExtension(args.name) && document.state === 'UPLOADING'
      );
    });
    if (sameNameDocIndex > -1) {
      state.documents.splice(sameNameDocIndex, 1);
    }
  },
  updateGeneric(state, args) {
    const objectsName = args.objectsName;
    const updatedObject = args.updatedObject;
    const objectList = state[objectsName];
    const newObjects =
      objectList && Array.isArray(objectList) ? [...state[objectsName]] : [];
    const indexToUpdate = newObjects.findIndex(
      (object) => object.id === updatedObject.id,
    );
    if (indexToUpdate > -1) {
      newObjects[indexToUpdate] = {
        ...newObjects[indexToUpdate],
        ...updatedObject,
      };
    } else {
      newObjects.unshift(updatedObject);
    }
    state[objectsName] = newObjects;
  },
  updateSelectedCustomer(state, payload) {
    if (state.customers && state.customers.selectedCustomer) {
      state.customers.selectedCustomer = {
        ...state.customers.selectedCustomer,
        ...payload,
      };
    }
  },
  addDocument(state, document) {
    const existingIndex = state.documents.findIndex(
      (d) => document.id && d.id.toString() === document.id.toString(),
    );
    if (existingIndex < 0) {
      state.documents = [document, ...(state.documents || [])];
    } else {
      state.documents[existingIndex] = document;
    }
  },
  setDocuments(state, documents) {
    if (!documents.reduce) {
      state.documents = documents;
    } else {
      state.documents = (documents || []).reduce(
        (newDocuments, document) => [
          ...newDocuments,
          {
            ...(state.documents || []).find(({ id }) => id === document.id),
            ...document,
          },
        ],
        [],
      );
    }
  },
  setDocumentsMetadata(state, metadata) {
    state.documentsMetadata = metadata;
  },
  setDocumentsLoading(state, loadingState) {
    state.documentsLoading = loadingState;
  },
  setSelectedDocumentId(state, documentId) {
    state.selectedDocumentId = documentId;
  },

  setSelectedPageId(state, pageId) {
    state.selectedPageId = pageId;
  },
  addTeamMember(state, newUser) {
    state.currentUser.organisation.users.unshift(newUser);
  },
  changeApiKeyStatus(state, key) {
    const updateStatus = state.currentUser.organisation.api_keys.map(
      (apiKeys) =>
        apiKeys.key === key ? { ...apiKeys, active: false } : apiKeys,
    );
    state.currentUser.organisation.api_keys = updateStatus;
  },
  updateApiKeyDetail(state, { key, name, description }) {
    const index = state.currentUser.organisation.api_keys.findIndex(
      (apiKeys) => {
        return apiKeys.key === key;
      },
    );
    state.currentUser.organisation.api_keys[index].name = name;
    state.currentUser.organisation.api_keys[index].description = description;
  },
  setWorkspaces(state, workspaces) {
    state.workspaces = workspaces;
  },
  updateWorkspaces(state, workspace) {
    state.workspaces.push(workspace);
  },
  setDocumentsBeingDeleted(state, document) {
    state.documentsBeingDeletedById[document.id] = true;
  },
  removeDocumentsBeingDeleted(state, document) {
    delete state.documentsBeingDeletedById[document.id];
  },
};

export const modules = {
  agents: AgentsStore,
  bulkUploads: BulkUploadStore,
  customers: CustomerStore,
  customersSignals: CustomersSignalsStore,
  collectSessions: CollectSessionsStore,
  collectConfig: CollectConfigStore,
  credit: CreditStore,
  documentsStore: DocumentsStore,
  documentHighlighter: DocumentHighlighterStore,
  documentParsedDetailsHighlighter: DocumentParsedDetailsHighlighterStore,
  documentFontsMetadata: DocumentFontsMetadataStore,
  config: ConfigStore,
  tags: TagStore,
  similarDocuments: SimilarDocumentsStore,
  featureFlagsStore: FeatureFlagsStore,
  queryStrings: QueryStringsStore,
  signalsStore: SignalsStore,
  searchStore: SearchStore,
  processingEventsStore: ProcessingEventsStore,
  chatStore: ChatStore,
};

export default createStore({
  modules,
  state: {
    documents: [],
    documentsMetadata: undefined,
    documentsLoading: false,
    selectedDocumentId: undefined,
    selectedPageId: undefined,
    currentUser: undefined,
    blocklist: [],
    templates: [],
    webhooks: [],
    hasUserCompletedRegistration: undefined,
    documentDifference: undefined,
    documentDifferenceMessage: undefined,
    identityProvider: undefined,
    selectedIdentityProviderId: undefined,
    documentDifferenceIsLoading: false,
    axiosCancelSource: axios.CancelToken.source(),
    workspaces: [],
    cachedDocumentsByCustomerId: {},
    documentsBeingDeletedById: {},
  },
  getters,
  actions,
  mutations,
});
